import React from 'react';
import { message, Modal } from 'antd';
import cookie from 'js-cookie';
import { TOKEN, REFRESH_TOKEN } from './constants/config';
import { refreshTokens } from './tokenRefresh';

/**
 * Launching a study in the AfyaNX desktop workstation (Weasis).
 *
 * Lives here, not in a page component, because BOTH the admin All Studies list
 * and the radiologist's My Worklist offer it. Two copies of this would drift —
 * and drift in this particular code means a radiologist gets a stale token, or
 * a silent no-op with no explanation, which is worse than not offering it.
 */

/** Strip the `{project-uuid}.` prefix the uploader adds to a StudyInstanceUID. */
export const normalizeStudyUID = (studyUID) => {
  if (!studyUID || typeof studyUID !== 'string') return '';
  if (studyUID.match(/^[a-f0-9-]{36}\./i)) {
    return studyUID.substring(37);
  }
  return studyUID;
};

/**
 * Fire a `weasis://` URL and, if nothing appears to have happened, explain why.
 *
 * A browser cannot be asked whether a protocol handler is registered — there is
 * no API for it, by design. So this infers: launching the workstation (or the
 * browser's own "Open AfyaNX Workstation?" prompt) takes focus away from the
 * page. If focus is still here a moment later, nothing handled it.
 */
export const launchWorkstation = (url) => {
  let handled = false;
  const markHandled = () => { handled = true; };

  // Any of these means something took over: the app launched, or the browser is asking.
  window.addEventListener('blur', markHandled, { once: true });
  window.addEventListener('pagehide', markHandled, { once: true });
  document.addEventListener('visibilitychange', markHandled, { once: true });

  window.location.href = url;

  window.setTimeout(() => {
    window.removeEventListener('blur', markHandled);
    window.removeEventListener('pagehide', markHandled);
    document.removeEventListener('visibilitychange', markHandled);
    if (handled || document.hidden) return;

    Modal.info({
      title: 'AfyaNX Workstation',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            If the desktop workstation did not open, it is probably not installed on this computer.
          </p>
          <p style={{ marginBottom: 0 }}>
            You can keep working in the web viewer using the <b>Read</b> button. To install the
            workstation, contact your administrator or Afya Tech at <b>bini2bk@gmail.com</b>.
          </p>
        </div>
      ),
      okText: 'Got it',
    });
  }, 2500);
};

/**
 * Resolve a study, refresh the session, and hand it to the desktop workstation.
 *
 * @param study            row object; needs `id` (Orthanc ID) and ideally studyInstanceUID
 * @param getStudyDetail   () => axios promise for /orthanc/studies/{id}
 */
export const openStudyInWorkstation = async (study, { getStudyDetail }) => {
  if (!study?.id) {
    message.error('Study ID not found');
    return;
  }
  try {
    const { data } = await getStudyDetail(study.id);
    const rawUID = data?.MainDicomTags?.StudyInstanceUID || study.studyInstanceUID;
    if (!rawUID) {
      message.error('StudyInstanceUID not found');
      return;
    }
    const hasSeries = data?.Series && Array.isArray(data.Series) && data.Series.length > 0;
    if (!hasSeries) {
      message.error('This study has incomplete DICOM data and cannot be opened.');
      return;
    }
    const resolvedUID = normalizeStudyUID(rawUID) || study.studyInstanceUID;

    // Refresh the access token so the desktop viewer starts its (possibly long)
    // study download with a full TTL.  Refresh tokens are single-use, so this
    // goes through the shared browser-wide refresh (tokenRefresh.js) like every
    // other refresh.  Failure is non-fatal: the getStudyDetail above just
    // proved the current token still works.
    if (cookie.get(REFRESH_TOKEN)) {
      try {
        await refreshTokens();
      } catch (e) {
        console.warn('[WORKSTATION] token refresh failed, launching with current token', e);
      }
    }

    const token = cookie.get(TOKEN);
    if (!token) {
      message.error('Session expired — please log in again.');
      return;
    }
    const cmd =
      '$dicom:rs --url "' + window.origin + '/dicom-web"' +
      ' -r "studyUID=' + resolvedUID + '"' +
      ' -H "Authorization: Bearer ' + token + '"';
    // Protocol launch, not window.open — a weasis:// URL must not spawn a blank tab.
    launchWorkstation('weasis://' + encodeURIComponent(cmd));
  } catch (error) {
    console.error('[WORKSTATION] Error launching desktop viewer:', error);
    message.error('Failed to open study in desktop workstation');
  }
};
